import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export function Modal({ open, title, onClose, children, footer, size = 'md' }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open) el.showModal();
    else if (el.open) el.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 m-auto rounded-wl shadow-modal border border-neutral-200 p-0 backdrop:bg-black/40 w-full"
      style={{ maxWidth: size === 'sm' ? '24rem' : size === 'md' ? '32rem' : size === 'lg' ? '42rem' : '64rem' }}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
        <h3 className="font-semibold text-neutral-900">{title}</h3>
        <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="px-5 py-4">{children}</div>
      {footer && (
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-neutral-200 bg-neutral-50">
          {footer}
        </div>
      )}
    </dialog>
  );
}
